import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Users, ScanFace, Check } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useLanguage } from "@/i18n";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const FaceSelectModal = ({ open, onOpenChange, imageSrc, selectedFaces = [], onApply }) => {
  const { t } = useLanguage();
  const [faces, setFaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState([]); // array of face ids
  const [instructions, setInstructions] = useState({}); // { [id]: text }

  const detect = useCallback(async () => {
    if (!imageSrc) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat/detect-faces`, { image_base64: imageSrc });
      setFaces(res.data.faces || []);
    } catch (e) {
      setFaces([]);
    } finally {
      setLoading(false);
    }
  }, [imageSrc]);

  useEffect(() => {
    if (open) {
      detect();
      setPicked((selectedFaces || []).map((f) => f.id));
      const init = {};
      (selectedFaces || []).forEach((f) => { if (f.instruction) init[f.id] = f.instruction; });
      setInstructions(init);
    }
  }, [open, detect]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => {
    setPicked((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  const apply = () => {
    onApply(
      faces
        .filter((f) => picked.includes(f.id))
        .map((f) => ({ ...f, instruction: (instructions[f.id] || "").trim() }))
    );
    onOpenChange(false);
  };

  const editEveryone = () => {
    onApply([]);
    onOpenChange(false);
  };

  const pickedFaces = faces.filter((f) => picked.includes(f.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto bg-[var(--bg-secondary)] border-[var(--border-color)]" data-testid="face-select-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[var(--text-primary)]">
            <ScanFace size={20} className="text-indigo-400" /> {t("face_select_title")}
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-[var(--text-secondary)]">{t("face_select_desc_multi")}</p>

        <div className="relative inline-block max-w-full mx-auto">
          <img src={imageSrc} alt="to edit" className="max-w-full max-h-[45vh] rounded-lg block" data-testid="face-select-image" />
          {!loading && faces.map((f) => {
            const active = picked.includes(f.id);
            return (
              <button
                key={f.id}
                onClick={() => toggle(f.id)}
                data-testid={`face-box-${f.id}`}
                title={f.label}
                style={{
                  left: `${f.box[0] * 100}%`,
                  top: `${f.box[1] * 100}%`,
                  width: `${f.box[2] * 100}%`,
                  height: `${f.box[3] * 100}%`,
                }}
                className={`absolute rounded-md transition-all duration-150 flex items-start justify-end
                  ${active ? "border-[3px] border-indigo-400 bg-indigo-400/25"
                           : "border-2 border-white/80 bg-white/5 hover:bg-indigo-400/25 hover:border-indigo-300"}`}
              >
                {active && (
                  <span className="m-0.5 bg-indigo-500 text-white rounded-full p-0.5 shadow">
                    <Check size={10} strokeWidth={3} />
                  </span>
                )}
                <span className="absolute left-1/2 -translate-x-1/2 top-full mt-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-600 text-white whitespace-nowrap shadow">
                  {f.label}
                </span>
              </button>
            );
          })}
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg">
              <span className="flex items-center gap-2 text-white text-sm">
                <Loader2 size={16} className="animate-spin" /> {t("face_detecting")}
              </span>
            </div>
          )}
        </div>

        {!loading && faces.length === 0 && (
          <p className="text-sm text-amber-400" data-testid="face-none">{t("face_none_found")}</p>
        )}

        {/* Per-person instructions */}
        {pickedFaces.length > 0 && (
          <div className="space-y-2" data-testid="per-person-instructions">
            <p className="text-xs text-[var(--text-muted)]">{t("face_per_person_hint")}</p>
            {pickedFaces.map((f) => (
              <div key={f.id} className="flex items-center gap-2">
                <span className="text-xs font-medium text-indigo-300 w-28 shrink-0 truncate">{f.label}</span>
                <input
                  value={instructions[f.id] || ""}
                  onChange={(e) => setInstructions((prev) => ({ ...prev, [f.id]: e.target.value }))}
                  placeholder={t("face_instruction_placeholder")}
                  data-testid={`face-instruction-${f.id}`}
                  className="flex-1 bg-[var(--input-bg)] border border-[var(--border-color)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap justify-between items-center gap-2 pt-1">
          <Button
            variant="outline"
            onClick={editEveryone}
            className="border-[var(--border-color)] text-[var(--text-secondary)]"
            data-testid="face-select-all"
          >
            <Users size={16} className="mr-1.5" /> {t("face_edit_everyone")}
          </Button>
          <Button
            onClick={apply}
            disabled={picked.length === 0}
            className="bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50"
            data-testid="face-apply-selection"
          >
            <Check size={16} className="mr-1.5" />
            {picked.length > 0 ? t("face_apply_n").replace("{n}", picked.length) : t("face_apply")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FaceSelectModal;

